import { useCallback, useEffect, useState } from "react";
import { ABOUT_SELFIE_COLLAGE_SLOTS } from "../../data/aboutSelfies";
import { useAboutSelfies } from "../../hooks/useAboutSelfies";
import { photoUrl } from "../../lib/assets";

/** @typedef {import("../../data/aboutSelfies").AboutSelfie} AboutSelfie */

const MASONRY_SLOT_COUNT = ABOUT_SELFIE_COLLAGE_SLOTS.length;

/**
 * @param {AboutSelfie[]} selfies
 */
function splitSelfiesForLayout(selfies) {
  const masonryGroups = [];

  for (let index = 0; index + MASONRY_SLOT_COUNT <= selfies.length; index += MASONRY_SLOT_COUNT) {
    masonryGroups.push({
      start: index,
      items: selfies.slice(index, index + MASONRY_SLOT_COUNT),
    });
  }

  const remainderStart = masonryGroups.length * MASONRY_SLOT_COUNT;
  const remainder =
    remainderStart < selfies.length
      ? { start: remainderStart, items: selfies.slice(remainderStart) }
      : null;

  return { masonryGroups, remainder };
}

function LightboxChevron({ direction }) {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`about-selfies-lightbox__chevron about-selfies-lightbox__chevron--${direction}`}
    >
      {direction === "prev" ? <path d="M15 6 9 12l6 6" /> : <path d="M9 6l6 6-6 6" />}
    </svg>
  );
}

/**
 * @param {{
 *   selfie: AboutSelfie,
 *   index: number,
 *   slotClass?: string,
 *   onOpen?: (index: number) => void,
 * }} props
 */
function AboutSelfieTile({ selfie, index, slotClass = "", onOpen }) {
  return (
    <button
      type="button"
      className={`about-selfies__tile ${slotClass}`.trim()}
      aria-label={`Open selfie ${index + 1}`}
      onClick={() => onOpen?.(index)}
    >
      <span className="about-selfies__tile-frame">
        <img
          src={photoUrl(selfie)}
          alt=""
          loading="lazy"
          className="about-selfies__tile-image"
        />
      </span>
    </button>
  );
}

/**
 * @param {{
 *   group: { start: number, items: AboutSelfie[] },
 *   onOpen?: (index: number) => void,
 * }} props
 */
function AboutSelfieMasonryGroup({ group, onOpen }) {
  return (
    <div className="about-selfies__collage">
      {group.items.map((selfie, index) => (
        <AboutSelfieTile
          key={selfie.id}
          selfie={selfie}
          index={group.start + index}
          slotClass={`about-selfies__tile--${ABOUT_SELFIE_COLLAGE_SLOTS[index]}`}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

/**
 * @param {{
 *   group: { start: number, items: AboutSelfie[] },
 *   onOpen?: (index: number) => void,
 * }} props
 */
function AboutSelfieRemainderGrid({ group, onOpen }) {
  return (
    <div className="about-selfies__collage about-selfies__collage--remainder">
      {group.items.map((selfie, index) => (
        <AboutSelfieTile
          key={selfie.id}
          selfie={selfie}
          index={group.start + index}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

/**
 * @param {{
 *   selfies: AboutSelfie[],
 *   onOpen?: (index: number) => void,
 * }} props
 */
function AboutSelfieCollageGrid({ selfies, onOpen }) {
  if (selfies.length === 0) {
    return null;
  }

  const { masonryGroups, remainder } = splitSelfiesForLayout(selfies);

  return (
    <div className="about-selfies__collages">
      {masonryGroups.map((group) => (
        <AboutSelfieMasonryGroup key={group.start} group={group} onOpen={onOpen} />
      ))}
      {remainder ? <AboutSelfieRemainderGrid group={remainder} onOpen={onOpen} /> : null}
    </div>
  );
}

/**
 * @param {{
 *   selfies: AboutSelfie[],
 *   index: number,
 *   onClose: () => void,
 *   onChangeIndex: (index: number) => void,
 * }} props
 */
function AboutSelfieLightbox({ selfies, index, onClose, onChangeIndex }) {
  const selfie = selfies[index];
  const count = selfies.length;

  const navigate = useCallback(
    (delta) => {
      onChangeIndex((index + delta + count) % count);
    },
    [count, index, onChangeIndex]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowLeft") {
        navigate(-1);
      } else if (event.key === "ArrowRight") {
        navigate(1);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [navigate, onClose]);

  if (!selfie) {
    return null;
  }

  const indexLabel = `${String(index + 1).padStart(2, "0")} / ${String(count).padStart(2, "0")}`;

  return (
    <div
      className="about-selfies-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Selfie ${index + 1} of ${count}`}
      onClick={onClose}
    >
      <div className="about-selfies-lightbox__top">
        <span className="about-selfies-lightbox__index">{indexLabel}</span>
        <button
          type="button"
          className="about-selfies-lightbox__close"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          CLOSE <span aria-hidden="true">×</span>
        </button>
      </div>

      <div
        className="about-selfies-lightbox__stage"
        onClick={(event) => event.stopPropagation()}
      >
        <img
          src={photoUrl(selfie)}
          alt={`Selfie ${index + 1}`}
          className="about-selfies-lightbox__image"
        />
      </div>

      {count > 1 ? (
        <>
          <button
            type="button"
            className="about-selfies-lightbox__arrow about-selfies-lightbox__arrow--prev"
            aria-label="Previous photo"
            onClick={(event) => {
              event.stopPropagation();
              navigate(-1);
            }}
          >
            <LightboxChevron direction="prev" />
          </button>
          <button
            type="button"
            className="about-selfies-lightbox__arrow about-selfies-lightbox__arrow--next"
            aria-label="Next photo"
            onClick={(event) => {
              event.stopPropagation();
              navigate(1);
            }}
          >
            <LightboxChevron direction="next" />
          </button>
        </>
      ) : null}
    </div>
  );
}

/**
 * @param {{ selfies?: AboutSelfie[] }} props
 */
export default function AboutSelfieCollage({ selfies: selfiesProp }) {
  const { selfies: fetchedSelfies, loading } = useAboutSelfies();
  const selfies = selfiesProp ?? fetchedSelfies;
  const [lightboxIndex, setLightboxIndex] = useState(/** @type {number | null} */ (null));

  if (loading && selfies.length === 0) {
    return null;
  }

  if (selfies.length === 0) {
    return null;
  }

  return (
    <>
      <AboutSelfieCollageGrid
        selfies={selfies}
        onOpen={(index) => setLightboxIndex(index)}
      />
      {lightboxIndex !== null ? (
        <AboutSelfieLightbox
          selfies={selfies}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChangeIndex={setLightboxIndex}
        />
      ) : null}
    </>
  );
}
